import { useTranslation } from 'react-i18next'
import { useJsonLd } from '@/application/hooks/useJsonLd'
import { resolveLanguage } from '@/application/i18n/language'
import { localizedAbsoluteUrl } from '@/application/seo/site'
import type { BreadcrumbItem } from '@/ui/components/common/Breadcrumb'

/**
 * `BreadcrumbList` JSON-LD for a public page, derived from the EXACT SAME
 * `items` prop the visible `Breadcrumb` renders — so the structured data and
 * what a visitor actually sees can never disagree.
 *
 * The last item (the current page) commonly has no `href` — `Breadcrumb`
 * renders it as plain, non-clickable text — but `BreadcrumbList` requires an
 * absolute `item` URL for every entry, so a missing `href` falls back to
 * `currentUrl`: the current page's own already-localized, already-absolute
 * URL (the same value passed to `useDocumentMeta`'s `url`, e.g. via
 * `useLocalizedUrl`).
 */
export function BreadcrumbJsonLd({
  items,
  currentUrl,
}: {
  items: BreadcrumbItem[]
  currentUrl: string
}) {
  const { i18n } = useTranslation()
  const lang = resolveLanguage(i18n.language)

  useJsonLd(
    'ld-json-breadcrumb',
    items.length
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.label,
            item: item.href ? localizedAbsoluteUrl(item.href, lang) : currentUrl,
          })),
        }
      : null,
  )

  return null
}
